import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ClipboardList, ShieldCheck, Clock, Award } from "lucide-react";
import { waLink, DEFAULT_WA_MSG } from "../data/content";
import { scrollToId } from "./Header";

const LINES = ["AMBIENTES", "BLINDADOS", "CONTRA PRAGAS"];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowO = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-16"
    >
      <motion.div
        style={{ opacity: glowO }}
        className="absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-gold/15 blur-[140px] pointer-events-none"
      />
      <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7 relative z-10 min-w-0">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[11px] sm:text-xs font-mono uppercase tracking-[0.3em] text-gold mb-6"
          >
            Belo Horizonte e Região Metropolitano · Minas Gerais
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[clamp(2.1rem,8.8vw,4.6rem)]">
            <MaskedLine delay={0.2}>{LINES[0]}</MaskedLine>
            <MaskedLine delay={0.32}>
              <span className="gold-text">{LINES[1]}</span>
            </MaskedLine>
            <MaskedLine delay={0.44}>{LINES[2]}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-mist"
          >
            Dedetização, desratização, descupinização e higienização técnica
            para residências, condomínios e indústrias — com equipe própria,
            produtos certificados e garantia de serviço.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              data-testid="hero-whatsapp-button"
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-4 font-bold text-ink text-sm sm:text-base transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
            >
              <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
              Orçamento grátis no WhatsApp
            </a>
            <button
              data-testid="hero-form-button"
              onClick={() => scrollToId("orcamento")}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/20 px-7 py-4 font-semibold text-sm sm:text-base text-zinc-100 hover:border-gold/60 hover:text-gold-light transition-colors duration-300"
            >
              <ClipboardList className="h-5 w-5" />
              Calcular orçamento online
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs sm:text-sm text-mist"
          >
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-gold" /> Produtos certificados
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-gold" /> Atendimento 24h
            </span>
            <span className="inline-flex items-center gap-2">
              <Award className="h-4 w-4 text-gold" /> Garantia de serviço
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative min-w-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-gold/40 via-transparent to-gold/10 blur-sm" />
              <div className="relative overflow-hidden rounded-[2rem] border border-gold/25">
                <img
                  src="/images/hero-bg.webp"
                  alt="Técnico Gold Service em campo"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl bg-black/60 backdrop-blur-md border border-white/10 px-4 py-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                      Gold Service
                    </p>
                    <p className="text-xs text-zinc-300">Equipe técnica própria</p>
                  </div>
                  <img
                    src="/images/logo.webp"
                    alt=""
                    className="h-9 w-9 rounded-full object-cover ring-1 ring-gold/40"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
